import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Link } from "react-router-dom";
import { RiArrowRightLine, RiArrowUpLine, RiSparkling2Line } from "@remixicon/react";
import api from "@/utils/api";
import { getAnalyticsRange } from "@/lib/analytics-range";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionCard } from "@/components/dashboard/shared/SectionCard";
import { cn } from "@/lib/utils";

type FunnelStep = {
  stage: string;
  label: string;
  visitors: number;
  dropoff: number;
  dropoffRate: number;
  conversionFromStart: number;
};

type TimelineDay = { date: string; visits: number; unique: number };

type SmartReport = {
  brief: string;
  funnelReady: boolean;
  visitors: { all: number; unique: number; returning: number };
  purchases: number;
  revenueLabel: string;
  comparison: { comparable: boolean; changePct: number };
  funnel: FunnelStep[];
  timeline: TimelineDay[];
  devices: { name: string; visits: number; checkouts: number; share: number }[];
  locations: { country: string; city: string; visits: number; share: number }[];
  browsers: { name: string; visits: number; share: number }[];
  referrers: { name: string; visits: number }[];
  languages: { name: string; visits: number }[];
  hours: { peak: { hour?: number; label: string; visits: number } | null; series: { hour: number; label: string; visits: number }[] };
  insights: { id: string; tone: "good" | "warn" | "neutral"; title: string; body: string }[];
  prompts: string[];
};

type Turn = { role: "user" | "assistant"; title?: string; text: string };

type Note = { question: string; answer: Turn | null };

const DEVICE_COLOR: Record<string, string> = {
  Mobile: "#6D28D9",
  Desktop: "#1C1C1A",
  Tablet: "#BE9AFC",
};

export function SmartAnalytics({
  timeRange,
  storeId,
}: {
  timeRange: string;
  storeId: string;
}) {
  const range = getAnalyticsRange(timeRange);
  const [draft, setDraft] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  const threadRef = useRef<HTMLDivElement>(null);

  const report = useQuery({
    queryKey: ["smart-analytics", timeRange, storeId],
    enabled: Boolean(storeId),
    queryFn: async () => {
      const params = new URLSearchParams(range);
      const { data } = await api.get<SmartReport>(`/api/analytics/smart?${params.toString()}`, {
        headers: { "Store-ID": storeId },
      });
      return data;
    },
  });

  useEffect(() => {
    setTurns([]);
    setDraft("");
  }, [timeRange, storeId]);

  useEffect(() => {
    const thread = threadRef.current;
    if (!thread || turns.length === 0) return;
    thread.scrollTop = thread.scrollHeight;
  }, [turns, report.data]);

  const ask = useMutation({
    mutationFn: async (question: string) => {
      const params = new URLSearchParams(range);
      const { data } = await api.post<{ title: string; answer: string }>(
        `/api/analytics/smart/ask?${params.toString()}`,
        { question },
        { headers: { "Store-ID": storeId } },
      );
      return data;
    },
    onSuccess: (data) => {
      setTurns((current) => [...current, { role: "assistant", title: data.title, text: data.answer }]);
    },
    onError: () => {
      setTurns((current) => [
        ...current,
        { role: "assistant", text: "I couldn't read that just now. Ask again in a moment." },
      ]);
    },
  });

  const send = (question: string) => {
    const text = question.trim().slice(0, 400);
    if (!text || ask.isPending) return;
    setDraft("");
    setTurns((current) => [...current, { role: "user", text }]);
    ask.mutate(text);
  };

  if (report.isLoading) return <SmartLoading />;

  if (isAxiosError(report.error) && report.error.response?.status === 403) {
    return <PremiumGate />;
  }

  if (report.isError || !report.data) {
    return (
      <SectionCard className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="font-heading text-base font-bold">This period didn't load</p>
          <p className="mt-1 text-sm text-muted-foreground">The read is still there. Try it again.</p>
        </div>
        <Button variant="outline" className="rounded-full" onClick={() => report.refetch()}>
          Try again
        </Button>
      </SectionCard>
    );
  }

  const data = report.data;
  const quiet = data.visitors.all === 0 && data.purchases === 0;
  const notes = groupNotes(turns);
  const purchase = data.funnel.find((step) => step.stage === "purchase");
  const checkout = data.funnel.find((step) => step.stage === "checkout");

  return (
    <div className="space-y-4">
      <section className="overflow-hidden rounded-[1.5rem] border border-[#EBEBEB] bg-white">
        <div className="px-5 pb-5 pt-5 sm:px-6 sm:pt-6">
          <div className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-full bg-[#EDE4FB] text-primary">
              <RiSparkling2Line className="size-3.5" />
            </span>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Smart read</p>
          </div>
          <p className="mt-4 max-w-3xl text-[15px] font-medium leading-7 text-foreground sm:text-base sm:leading-8">
            {data.brief}
          </p>
        </div>

        {notes.length > 0 && (
          <div
            ref={threadRef}
            className="max-h-72 space-y-5 overflow-y-auto border-t border-[#EBEBEB] px-5 py-5 sm:px-6"
            aria-live="polite"
          >
            {notes.map((note, index) => (
              <article key={`${note.question}-${index}`}>
                {note.question ? (
                  <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{note.question}</p>
                ) : null}
                {note.answer ? (
                  <div className={note.question ? "mt-2" : undefined}>
                    {note.answer.title ? <p className="font-heading text-sm font-bold">{note.answer.title}</p> : null}
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">{note.answer.text}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-muted-foreground">Reading this period…</p>
                )}
              </article>
            ))}
          </div>
        )}

        <div className="border-t border-[#EBEBEB] bg-[#FAFAF8] px-5 py-4 sm:px-6">
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {data.prompts.map((prompt) => (
              <button
                key={prompt}
                type="button"
                disabled={ask.isPending}
                onClick={() => send(prompt)}
                className="shrink-0 rounded-full border border-[#EBEBEB] bg-white px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-black/15 hover:bg-[#F5F5F3] disabled:opacity-50"
              >
                {prompt}
              </button>
            ))}
          </div>
          <form
            className="mt-3 flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              send(draft);
            }}
          >
            <label htmlFor="smart-ask" className="sr-only">
              Ask about this period
            </label>
            <input
              id="smart-ask"
              value={draft}
              maxLength={400}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Ask about visitors, drop-off, devices, or locations"
              className="h-11 min-w-0 flex-1 rounded-full border border-[#EBEBEB] bg-white px-4 text-sm outline-none transition-shadow placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/20"
            />
            <Button
              type="submit"
              size="icon"
              className="size-11 shrink-0 rounded-full"
              disabled={ask.isPending || !draft.trim()}
              aria-label="Ask"
            >
              <RiArrowUpLine className="size-4" />
            </Button>
          </form>
        </div>
      </section>

      <div className="grid grid-cols-2 overflow-hidden rounded-[1.25rem] border border-[#EBEBEB] bg-white xl:grid-cols-4">
        <Metric
          index={0}
          label="Opens"
          value={formatCount(data.visitors.all)}
          hint={data.comparison.comparable ? "Compared with the previous period" : "Times the link was opened"}
          change={data.comparison.comparable ? data.comparison.changePct : null}
        />
        <Metric
          index={1}
          label="Unique visitors"
          value={formatCount(data.visitors.unique)}
          hint={data.visitors.returning ? `${formatCount(data.visitors.returning)} came back` : "People counted once"}
        />
        <Metric
          index={2}
          label="Reached checkout"
          value={checkout && (data.funnelReady || checkout.visitors > 0) ? formatCount(checkout.visitors) : "—"}
          hint={data.funnelReady ? "Tracked people" : "Still recording"}
        />
        <Metric
          index={3}
          label="Orders"
          value={formatCount(data.purchases)}
          hint={data.revenueLabel}
        />
      </div>

      {data.insights.length > 0 && (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.insights.map((insight) => (
            <article key={insight.id} className="rounded-[1.25rem] border border-[#EBEBEB] bg-white p-4">
              <div className="flex items-start gap-2.5">
                <span className={cn("mt-1.5 size-1.5 shrink-0 rounded-full", toneDot(insight.tone))} />
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold leading-snug">{insight.title}</p>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{insight.body}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {!quiet && (
        <>
          <Funnel data={data} purchaseRate={purchase?.conversionFromStart || 0} />
          <Timeline days={data.timeline} />
          <div className="grid gap-4 lg:grid-cols-2">
            <Devices devices={data.devices} />
            <RankCard
              title="Locations"
              empty="Location is attached when someone opens the live link."
              rows={data.locations.slice(0, 6).map((place) => ({
                label: placeLabel(place.city, place.country),
                value: formatCount(place.visits),
                share: place.share,
              }))}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <RankCard
              title="Sources"
              empty="Sources appear as new visits come in."
              rows={shareRows(data.referrers)}
            />
            <RankCard
              title="Browsers"
              empty="Browser names show up on new visits."
              rows={data.browsers.slice(0, 6).map((item) => ({
                label: item.name,
                value: formatCount(item.visits),
                share: item.share,
              }))}
            />
          </div>
          <div className="grid gap-4 lg:grid-cols-[1.3fr_0.7fr]">
            <Hours hours={data.hours} />
            <Languages languages={data.languages} />
          </div>
        </>
      )}
    </div>
  );
}

function Metric({
  index,
  label,
  value,
  hint,
  change = null,
}: {
  index: number;
  label: string;
  value: string;
  hint: string;
  change?: number | null;
}) {
  return (
    <div
      className={cn(
        "p-4 sm:p-5",
        index % 2 === 1 && "border-l border-[#EBEBEB]",
        index >= 2 && "border-t border-[#EBEBEB]",
        index > 0 && "xl:border-l xl:border-[#EBEBEB]",
        "xl:border-t-0",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
        {change != null && <Change value={change} />}
      </div>
      <p className="mt-2 font-heading text-[1.65rem] font-bold leading-none tracking-tight tabular-nums">{value}</p>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{hint}</p>
    </div>
  );
}

function Change({ value }: { value: number }) {
  const up = value > 0;
  const down = value < 0;
  const label = up ? `Up ${formatPct(value)}%` : down ? `Down ${formatPct(Math.abs(value))}%` : "Flat";
  return (
    <span
      className={cn(
        "rounded-full px-2 py-0.5 text-[11px] font-semibold tabular-nums",
        up && "bg-emerald-50 text-emerald-700",
        down && "bg-amber-50 text-amber-800",
        !up && !down && "bg-[#F5F5F3] text-muted-foreground",
      )}
    >
      {label}
    </span>
  );
}

function Funnel({ data, purchaseRate }: { data: SmartReport; purchaseRate: number }) {
  const opened = data.funnel[0]?.visitors || 0;
  return (
    <section className="rounded-[1.25rem] border border-[#EBEBEB] bg-white p-5 sm:p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-heading text-[15px] font-bold tracking-tight">From the link to the order</h2>
          <p className="mt-1 max-w-xl text-xs font-medium leading-relaxed text-muted-foreground">
            {data.funnelReady
              ? "Each person is counted once at a stage. The gap is how many did not continue."
              : "Opens and orders are ready. Cart and checkout fill in as new visits are tracked."}
          </p>
        </div>
        {opened > 0 && data.funnelReady && (
          <p className="shrink-0 rounded-full bg-[#EDE4FB] px-3 py-1.5 text-xs font-semibold text-primary">
            {formatPct(purchaseRate)}% ordered
          </p>
        )}
      </div>
      <ol className="mt-5 flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-1 [scrollbar-width:none] lg:grid lg:grid-cols-5 lg:overflow-visible [&::-webkit-scrollbar]:hidden">
        {data.funnel.map((step, index) => {
          const recording = !data.funnelReady && step.stage !== "visit" && step.stage !== "purchase" && step.visitors === 0;
          const width = opened > 0 ? Math.max(step.visitors > 0 ? 8 : 0, (step.visitors / opened) * 100) : 0;
          return (
            <li
              key={step.stage}
              className={cn(
                "flex min-h-40 w-[72%] shrink-0 snap-start flex-col rounded-2xl p-4 sm:w-52 lg:w-auto",
                step.stage === "purchase" ? "bg-[#EDE4FB]" : "bg-[#F5F5F3]",
                recording && "border border-dashed border-[#E0E0DC] bg-white",
              )}
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                {index + 1}. {step.label}
              </p>
              <p className="mt-3 font-heading text-3xl font-bold leading-none tabular-nums">
                {recording ? "—" : formatCount(step.visitors)}
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                {recording
                  ? "Recording"
                  : index === 0
                    ? "Everyone who opened the link"
                    : `${formatPct(step.conversionFromStart)}% of opens`}
              </p>
              <div className="mt-auto pt-4">
                {recording ? null : (
                  <div className="h-1.5 overflow-hidden rounded-full bg-black/5">
                    <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, width)}%` }} />
                  </div>
                )}
                {data.funnelReady ? (
                  <p
                    className={cn(
                      "mt-2 min-h-4 text-[11px] font-medium",
                      index > 0 && step.dropoff > 0 ? "text-amber-800" : "text-transparent",
                    )}
                    aria-hidden={!(index > 0 && step.dropoff > 0)}
                  >
                    {index > 0 && step.dropoff > 0
                      ? `${formatCount(step.dropoff)} left · ${formatPct(step.dropoffRate)}%`
                      : "\u00a0"}
                  </p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Timeline({ days }: { days: TimelineDay[] }) {
  const buckets = useMemo(() => bucketDays(days), [days]);
  const max = Math.max(1, ...buckets.map((day) => day.visits));
  const peakIndex = buckets.reduce((best, day, index, all) => (day.visits > all[best].visits ? index : best), 0);
  const [active, setActive] = useState<number | null>(null);
  const selected = buckets[active ?? peakIndex];

  useEffect(() => {
    setActive(null);
  }, [days]);

  return (
    <section className="rounded-[1.25rem] border border-[#EBEBEB] bg-white p-5 sm:p-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-heading text-[15px] font-bold tracking-tight">Over time</h2>
          <p className="mt-1 text-xs font-medium text-muted-foreground">Opens across this period.</p>
        </div>
        {selected && selected.visits > 0 && (
          <p className="text-sm font-medium tabular-nums">
            {selected.label}
            {selected.endLabel ? ` – ${selected.endLabel}` : ""}
            <span className="text-muted-foreground"> · {formatCount(selected.visits)} opens</span>
            {selected.unique != null ? <span className="text-muted-foreground"> · {formatCount(selected.unique)} unique</span> : null}
          </p>
        )}
      </div>
      {buckets.length === 0 ? (
        <p className="mt-6 text-sm text-muted-foreground">No opens in this period.</p>
      ) : (
        <>
          <div className="mt-6 flex h-40 items-end gap-1" role="img" aria-label="Opens over time">
            {buckets.map((day, index) => {
              const on = (active ?? peakIndex) === index;
              return (
                <button
                  key={`${day.date}-${index}`}
                  type="button"
                  className="flex h-full min-w-0 flex-1 items-end rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
                  aria-label={`${day.label}, ${formatCount(day.visits)} opens`}
                  onMouseEnter={() => setActive(index)}
                  onFocus={() => setActive(index)}
                  onClick={() => setActive(index)}
                >
                  <span
                    className={cn("block w-full rounded-t-md transition-colors", on ? "bg-primary" : "bg-[#DDD6FE]")}
                    style={{ height: `${Math.max(day.visits > 0 ? 6 : 2, (day.visits / max) * 100)}%` }}
                  />
                </button>
              );
            })}
          </div>
          <div className="mt-2 flex justify-between text-[11px] font-medium text-muted-foreground">
            <span>{buckets[0]?.label}</span>
            <span>{buckets[Math.floor((buckets.length - 1) / 2)]?.label}</span>
            <span>{buckets[buckets.length - 1]?.endLabel || buckets[buckets.length - 1]?.label}</span>
          </div>
        </>
      )}
    </section>
  );
}

function Devices({ devices }: { devices: SmartReport["devices"] }) {
  const recorded = devices.filter((device) => device.name !== "Not recorded" && device.visits > 0);
  const total = recorded.reduce((sum, device) => sum + device.visits, 0);
  return (
    <section className="rounded-[1.25rem] border border-[#EBEBEB] bg-white p-5 sm:p-6">
      <h2 className="font-heading text-[15px] font-bold tracking-tight">Devices</h2>
      {recorded.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">Device data shows up on new visits.</p>
      ) : (
        <>
          <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-[#F5F5F3]" role="img" aria-label="Share of opens by device">
            {recorded.map((device) => (
              <div
                key={device.name}
                style={{
                  width: `${(device.visits / total) * 100}%`,
                  backgroundColor: DEVICE_COLOR[device.name] || "#8A8A86",
                }}
              />
            ))}
          </div>
          <ul className="mt-4 space-y-3">
            {recorded.map((device) => (
              <li key={device.name} className="flex items-center gap-3">
                <span className="size-2.5 shrink-0 rounded-full" style={{ backgroundColor: DEVICE_COLOR[device.name] || "#8A8A86" }} />
                <span className="min-w-0 flex-1 text-sm font-medium">{device.name}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {formatCount(device.visits)} · {formatPct(device.share)}%
                  {device.checkouts > 0 ? ` · ${formatCount(device.checkouts)} checkouts` : ""}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function RankCard({
  title,
  rows,
  empty,
}: {
  title: string;
  rows: { label: string; value: string; share: number }[];
  empty: string;
}) {
  const visible = rows.filter((row) => row.label.trim());
  return (
    <section className="rounded-[1.25rem] border border-[#EBEBEB] bg-white p-5 sm:p-6">
      <h2 className="font-heading text-[15px] font-bold tracking-tight">{title}</h2>
      {visible.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">{empty}</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {visible.map((row, index) => (
            <li key={`${row.label}-${index}`}>
              <div className="mb-1.5 flex items-baseline justify-between gap-3">
                <span className="truncate text-sm font-medium">{row.label}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {row.value}
                  {row.share > 0 ? ` · ${formatPct(row.share)}%` : ""}
                </span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-[#F5F5F3]">
                <div className="h-full rounded-full bg-[#BE9AFC]" style={{ width: `${Math.max(6, Math.min(100, row.share))}%` }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function Hours({ hours }: { hours: SmartReport["hours"] }) {
  const series = Array.from({ length: 24 }, (_, hour) => {
    const match = hours.series.find((item) => item.hour === hour);
    return { hour, visits: match?.visits || 0, label: match?.label || hourLabel(hour) };
  });
  const max = Math.max(1, ...series.map((item) => item.visits));
  const peakHour = hours.peak?.hour ?? series.reduce((best, item) => (item.visits > best.visits ? item : best), series[0]).hour;
  const hasVisits = series.some((item) => item.visits > 0);

  return (
    <section className="rounded-[1.25rem] border border-[#EBEBEB] bg-white p-5 sm:p-6">
      <div className="flex items-end justify-between gap-3">
        <h2 className="font-heading text-[15px] font-bold tracking-tight">Local time</h2>
        {hours.peak ? (
          <p className="text-xs font-medium text-muted-foreground">
            Busiest at {hours.peak.label} · {formatCount(hours.peak.visits)}
          </p>
        ) : null}
      </div>
      {!hasVisits ? (
        <p className="mt-4 text-sm text-muted-foreground">A busy hour shows up once more visits are tracked.</p>
      ) : (
        <div className="mt-5 flex h-24 items-end gap-1" role="img" aria-label="Opens by local hour">
          {series.map((item) => (
            <div key={item.hour} className="flex h-full flex-1 items-end" title={`${item.label}: ${formatCount(item.visits)} opens`}>
              <div
                className={cn("w-full rounded-sm", item.hour === peakHour && item.visits > 0 ? "bg-primary" : "bg-[#DDD6FE]")}
                style={{ height: item.visits ? `${Math.max(8, (item.visits / max) * 100)}%` : "2px" }}
              />
            </div>
          ))}
        </div>
      )}
      {hasVisits && (
        <div className="mt-2 flex justify-between text-[11px] font-medium text-muted-foreground">
          <span>12am</span>
          <span>6am</span>
          <span>12pm</span>
          <span>6pm</span>
        </div>
      )}
    </section>
  );
}

function Languages({ languages }: { languages: SmartReport["languages"] }) {
  const rows = languages.filter((item) => item.name);
  return (
    <section className="rounded-[1.25rem] border border-[#EBEBEB] bg-white p-5 sm:p-6">
      <h2 className="font-heading text-[15px] font-bold tracking-tight">Languages</h2>
      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">Language settings are recorded with new visits.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {rows.map((item) => (
            <li key={item.name} className="flex items-center justify-between gap-3 rounded-2xl bg-[#F5F5F3] px-3.5 py-2.5">
              <span className="truncate text-sm font-medium">{item.name}</span>
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{formatCount(item.visits)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function PremiumGate() {
  return (
    <section className="overflow-hidden rounded-[1.5rem] border border-[#EBEBEB] bg-white">
      <div className="grid items-center gap-8 p-6 sm:p-8 lg:grid-cols-[1.1fr_0.9fr]">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary">Premium</p>
          <h2 className="mt-2 max-w-md font-heading text-2xl font-bold tracking-tight">Smart mode reads the sales link for you</h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-muted-foreground">
            See who opened the link, the device and place they came from, and the step where they leave on the way to checkout.
          </p>
          <Button asChild className="mt-5 rounded-full">
            <Link to="/dashboard/settings#billing">
              Upgrade to Premium
              <RiArrowRightLine className="size-4" />
            </Link>
          </Button>
        </div>
        <div className="rounded-[1.25rem] bg-[#F5F5F3] p-4" aria-hidden>
          <div className="grid grid-cols-3 gap-2">
            {["Opens", "Visitors", "Orders"].map((label) => (
              <div key={label} className="rounded-2xl bg-white px-3 py-3">
                <p className="text-[10px] font-medium text-muted-foreground">{label}</p>
                <div className="mt-2 h-4 w-10 rounded-full bg-[#EDE4FB]" />
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2 rounded-2xl bg-white p-3">
            {[100, 72, 48, 30, 18].map((width) => (
              <div key={width} className="h-2 rounded-full bg-[#EDE4FB]" style={{ width: `${width}%` }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SmartLoading() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading smart analytics">
      <Skeleton className="h-56 rounded-[1.5rem]" />
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-[1.25rem] border border-[#EBEBEB] bg-[#EBEBEB] xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-28 rounded-none" />
        ))}
      </div>
      <Skeleton className="h-52 rounded-[1.25rem]" />
      <Skeleton className="h-48 rounded-[1.25rem]" />
    </div>
  );
}

function groupNotes(turns: Turn[]): Note[] {
  const notes: Note[] = [];
  for (const turn of turns) {
    if (turn.role === "user") {
      notes.push({ question: turn.text, answer: null });
      continue;
    }
    const open = notes[notes.length - 1];
    if (open && !open.answer) open.answer = turn;
    else notes.push({ question: "", answer: turn });
  }
  return notes;
}

function bucketDays(days: TimelineDay[], maxBars = 36) {
  if (days.length === 0) return [];
  const size = days.length <= maxBars ? 1 : Math.ceil(days.length / maxBars);
  const buckets: { date: string; label: string; endLabel?: string; visits: number; unique: number | null }[] = [];
  for (let index = 0; index < days.length; index += size) {
    const slice = days.slice(index, index + size);
    const last = slice[slice.length - 1];
    buckets.push({
      date: slice[0].date,
      label: shortDate(slice[0].date),
      endLabel: slice.length > 1 ? shortDate(last.date) : undefined,
      visits: slice.reduce((sum, day) => sum + day.visits, 0),
      unique: slice.length === 1 ? slice[0].unique : null,
    });
  }
  return buckets;
}

function shortDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function hourLabel(hour: number) {
  const suffix = hour >= 12 ? "pm" : "am";
  return `${hour % 12 || 12}${suffix}`;
}

function shareRows(rows: { name: string; visits: number }[]) {
  const total = rows.reduce((sum, row) => sum + row.visits, 0);
  return rows.slice(0, 6).map((row) => ({
    label: row.name,
    value: formatCount(row.visits),
    share: total ? (row.visits / total) * 100 : 0,
  }));
}

function placeLabel(city: string, country: string) {
  if (city && country) return `${city}, ${country}`;
  return country || city || "Unknown";
}

function formatCount(value: number) {
  return (value || 0).toLocaleString();
}

function formatPct(value: number) {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

function toneDot(tone: "good" | "warn" | "neutral") {
  if (tone === "good") return "bg-emerald-500";
  if (tone === "warn") return "bg-amber-500";
  return "bg-primary";
}
